import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, MapPin, User } from 'lucide-react';
import { FormatTabs } from './FormatTabs';
import { RadioFormat } from './RadioFormat';
import { WhatsAppFormat } from './WhatsAppFormat';
import { SocialFormat } from './SocialFormat';
import { CopyableBlock } from './CopyableBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoryFormatCard = ({ story, token }) => {
  const [formats, setFormats] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('radio');

  const loadFormats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${story.id}/formats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load formats (${res.status})`);
      const data = await res.json();
      setMeta(data);
      setFormats(data.formats || {});
      const available = computeAvailable(data.formats || {});
      if (available.length && !available.includes(activeTab)) setActiveTab(available[0]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFormats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id]);

  const computeAvailable = (f) => {
    const keys = [];
    if (f.radio || f.radio_diary) keys.push('radio');
    if (f.whatsapp || f.whatsapp_broadcast) keys.push('whatsapp');
    if (f.social || f.social_burst) keys.push('social');
    if (f.evidence_brief || f.brief) keys.push('brief');
    return keys.length ? keys : ['radio', 'whatsapp', 'social'];
  };

  const available = formats ? computeAvailable(formats) : ['radio', 'whatsapp', 'social'];

  const radioData = formats?.radio || formats?.radio_diary;
  const whatsappData = formats?.whatsapp || formats?.whatsapp_broadcast;
  const socialData = formats?.social || formats?.social_burst;
  const briefData = formats?.evidence_brief || formats?.brief;

  return (
    <Card data-testid={`story-format-card-${story.id}`} className="overflow-hidden">
      <div className="grid md:grid-cols-[180px_1fr] gap-0">
        {story.image_url && (
          <div className="hidden md:block">
            <img
              src={story.image_url}
              alt={story.title}
              className="w-full h-full object-cover"
              style={{ minHeight: '100%' }}
            />
          </div>
        )}
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-serif text-xl font-bold text-gray-900 leading-tight">
                {story.title}
              </h3>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {story.author_name}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {story.country_name}
                </span>
                {story.story_archetype && (
                  <Badge className="bg-[#E4E517] text-gray-900 hover:bg-[#E4E517]">
                    {story.story_archetype}
                  </Badge>
                )}
              </div>
              {story.evidence_category && (
                <p className="text-xs text-gray-500 mt-1 italic">
                  Evidence: {story.evidence_category}
                </p>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-6 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading formats…
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 py-3" data-testid="format-error">
              {error}
            </p>
          )}

          {!loading && !error && formats && (
            <>
              <FormatTabs active={activeTab} onChange={setActiveTab} available={available} />
              {activeTab === 'radio' && <RadioFormat data={radioData} />}
              {activeTab === 'whatsapp' && <WhatsAppFormat data={whatsappData} />}
              {activeTab === 'social' && <SocialFormat data={socialData} />}
              {activeTab === 'brief' && (
                <CopyableBlock
                  label="Evidence Brief"
                  content={briefData?.brief || JSON.stringify(briefData, null, 2)}
                  mono
                  testId="copy-brief"
                />
              )}
            </>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

export default StoryFormatCard;
